import type { ComputedRef, InjectionKey, WritableComputedRef } from "vue";
import type { Filter, FilterPaginationState } from "shared/types";

/**
 * Filter type constants used throughout the filter system
 */
export const FILTER_TYPES = {
  SELECT: "select",
  RADIO: "radio",
  TEXT: "text",
  SEARCH: "search",
  SORT: "sort",
  TRANSITION_TRAFFICLIGHT_MULTI_SELECT: "transition-trafficlight-multi-select",
} as const;

export type FilterTypeValue = (typeof FILTER_TYPES)[keyof typeof FILTER_TYPES];

/**
 * Multi-select filter types that use arrays as values
 */
export const MULTI_SELECT_FILTER_TYPES: FilterTypeValue[] = [
  FILTER_TYPES.SELECT,
  FILTER_TYPES.TRANSITION_TRAFFICLIGHT_MULTI_SELECT,
];

/**
 * Check if a filter type uses array values
 */
export const isMultiSelectFilterType = (type: string): boolean =>
  MULTI_SELECT_FILTER_TYPES.includes(type as FilterTypeValue);

/**
 * Default pagination settings
 */
export const FILTER_PAGINATION_DEFAULTS = {
  PAGE: 1,
  PER_PAGE: 9,
} as const;

/**
 * Injection key for filter pagination context (provide/inject pattern)
 */
export const FILTER_PAGINATION_KEY = Symbol(
  "filter-pagination",
) as InjectionKey<FilterPaginationContext>;

/**
 * Context provided by FilterPanel for pagination
 */
export interface FilterPaginationContext {
  state: WritableComputedRef<FilterPaginationState>;
  isLoading: ComputedRef<boolean>;
  hasPagination: ComputedRef<boolean>;
  onUpdate: (val: FilterPaginationState) => void;
}

/**
 * Injection key for filter chips context (provide/inject pattern)
 */
export const FILTER_CHIPS_KEY = Symbol(
  "filter-chips",
) as InjectionKey<FilterChipsContext>;

/**
 * Context provided by FilterPanel for chips
 */
export interface FilterChipsContext {
  chipLabels: ComputedRef<Record<string, string>>;
  isLoading: ComputedRef<boolean>;
  hasChips: ComputedRef<boolean>;
  onRemove: (key: string) => void;
}

/**
 * Injection key for filter sort context (provide/inject pattern)
 */
export const FILTER_SORT_KEY = Symbol(
  "filter-sort",
) as InjectionKey<FilterSortContext>;

/**
 * Context provided by FilterPanel for the sort dropdown.
 *
 * Unlike the drawer/chips, the SORT-type filter is consumed by a dedicated
 * <filter-sort /> companion component. The chosen field rides to the backend
 * through the existing query-param path and never becomes a chip.
 */
export interface FilterSortContext {
  sortFilter: ComputedRef<Filter | null>;
  sortValue: WritableComputedRef<string | null>;
  hasSort: ComputedRef<boolean>;
  isLoading: ComputedRef<boolean>;
}
